import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Play, Instagram, Youtube, Mail, Phone, MapPin, Star, ArrowRight, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { portfolioData, categories } from '../mock';

const InstagramEmbed = ({ url, title }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Faster loading for mobile - immediate visibility, no lazy load delay
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      // On mobile, show immediately
      setIsVisible(true);
    } else {
      // On desktop, use lazy loading
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1, rootMargin: '100px' }
      );
      
      const element = document.getElementById(`embed-${url}`);
      if (element) observer.observe(element);
      
      return () => observer.disconnect();
    }
  }, [url]);
  
  const getEmbedUrl = (instagramUrl) => {
    const match = instagramUrl.match(/\/(p|reel)\/([^/?]+)/);
    if (match) {
      const type = match[1];
      const id = match[2];
      return `https://www.instagram.com/${type}/${id}/embed/`;
    }
    return null;
  };

  const embedUrl = getEmbedUrl(url);

  if (!embedUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-800">
        <p className="text-gray-400">Invalid Instagram URL</p>
      </div>
    );
  }

  return (
    <div 
      id={`embed-${url}`}
      className="relative w-full bg-[#1a1a1a]" 
      style={{ paddingBottom: '125%' }}
    >
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-black animate-pulse">
          <div className="flex flex-col items-center">
            <div className="w-10 h-10 border-3 border-white/20 border-t-white rounded-full animate-spin mb-2"></div>
            <p className="text-gray-400 text-xs">Loading...</p>
          </div>
        </div>
      )}
      {isVisible && (
        <iframe
          src={embedUrl}
          className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          frameBorder="0"
          scrolling="no"
          allow="encrypted-media"
          title={title}
          loading="eager"
          onLoad={() => setIsLoaded(true)}
        />
      )}
    </div>
  );
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    message: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section[id]');
    sections.forEach(section => observer.observe(section));

    return () => sections.forEach(section => observer.unobserve(section));
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      
      // Call backend API
      const response = await axios.post(`${API}/bookings`, formData);
      
      if (response.data) {
        toast({
          title: "Booking Request Sent! ✅",
          description: "Thank you for your interest. I'll get back to you within 24 hours.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast({
        title: "Error Sending Request ❌",
        description: "Something went wrong. Please try again or contact me directly via email.",
        variant: "destructive"
      });
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <button 
              onClick={() => scrollToSection('home')}
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_edit-and-book/artifacts/sfvr25mg_es_play_bardh.png" 
                alt="ES Logo" 
                className="h-10 w-auto"
              />
              <span className="hidden sm:block text-white font-bold text-xl tracking-wide">Edon Stavileci</span>
            </button>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-white hover:text-gray-300 transition-colors duration-300 font-light">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-white hover:text-gray-300 transition-colors duration-300 font-light">About</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-white hover:text-gray-300 transition-colors duration-300 font-light">Portfolio</button>
              <button onClick={() => scrollToSection('services')} className="text-white hover:text-gray-300 transition-colors duration-300 font-light">Services</button>
              <button onClick={() => scrollToSection('booking')} className="text-white hover:text-gray-300 transition-colors duration-300 font-light">Contact</button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t border-white/10">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-2 text-white hover:text-gray-300 transition-colors">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-white hover:text-gray-300 transition-colors">About</button>
              <button onClick={() => scrollToSection('portfolio')} className="block w-full text-left py-2 text-white hover:text-gray-300 transition-colors">Portfolio</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-white hover:text-gray-300 transition-colors">Services</button>
              <button onClick={() => scrollToSection('booking')} className="block w-full text-left py-2 text-white hover:text-gray-300 transition-colors">Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)] animate-pulse"></div>
        </div>
        
        {/* Large logo in background */}
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <img 
            src="https://customer-assets.emergentagent.com/job_edit-and-book/artifacts/sfvr25mg_es_play_bardh.png" 
            alt="Background Logo" 
            className="w-[800px] h-auto object-contain"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8 inline-block animate-fade-in">
            <img 
              src="https://customer-assets.emergentagent.com/job_edit-and-book/artifacts/sfvr25mg_es_play_bardh.png" 
              alt="Logo" 
              className="w-32 h-32 mx-auto"
            />
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-4 tracking-tight text-white animate-slide-up">
            {portfolioData.hero.name}
          </h1>
          <p className="text-2xl md:text-3xl text-gray-300 mb-4 font-light tracking-wide animate-slide-up" style={{ animationDelay: '0.2s' }}>
            {portfolioData.hero.tagline}
          </p>
          <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl mx-auto animate-slide-up" style={{ animationDelay: '0.4s' }}>
            {portfolioData.hero.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.6s' }}>
            <Button 
              onClick={() => scrollToSection('portfolio')}
              className="bg-white hover:bg-gray-200 text-black font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
            >
              {portfolioData.hero.ctaText}
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              onClick={() => scrollToSection('booking')}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
            >
              Book Now
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className={`py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 ${isVisible.about ? 'section-visible' : 'section-hidden'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">{portfolioData.about.title}</h2>
            <div className="w-24 h-1 bg-white mx-auto"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-left">
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                {portfolioData.about.description}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {portfolioData.about.expertise.map((skill, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                    <span className="text-gray-300">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-6 animate-slide-in-right">
              {portfolioData.about.stats.map((stat, index) => (
                <Card key={index} className="bg-black/50 border-white/20 backdrop-blur-sm hover:border-white/40 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">My Work</h2>
            <div className="w-24 h-1 bg-white mx-auto mb-8"></div>
            <p className="text-lg text-gray-400">Explore my latest video projects</p>
          </div>

          {/* Video Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioData.videos.map((video, index) => (
              <div
                key={video.id}
                className="opacity-100"
              >
                <Card className="bg-gradient-to-br from-gray-900 to-black border-white/10 overflow-hidden hover:border-white/30 transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-white/10 group">
                  <div className="relative">
                    <InstagramEmbed url={video.instagramUrl} title={video.title} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className={`py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 ${isVisible.services ? 'section-visible' : 'section-hidden'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Services</h2>
            <div className="w-24 h-1 bg-white mx-auto mb-8"></div>
            <p className="text-lg text-gray-400">What I offer</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {portfolioData.services.map((service, index) => (
              <Card 
                key={index} 
                className="bg-black/50 border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105 backdrop-blur-sm animate-scale-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3 text-white">{service.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-300">
                        <div className="w-1.5 h-1.5 bg-white rounded-full mr-2"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Client Testimonials</h2>
            <div className="w-24 h-1 bg-white mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {portfolioData.testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="bg-gradient-to-br from-gray-900 to-black border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105"
              >
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="fill-white text-white" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <div className="font-semibold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section id="booking" className={`py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 ${isVisible.booking ? 'section-visible' : 'section-hidden'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Book Your Project</h2>
            <div className="w-24 h-1 bg-white mx-auto mb-8"></div>
            <p className="text-lg text-gray-400">Let's bring your vision to life</p>
          </div>

          <Card className="bg-black/50 border-white/10 backdrop-blur-sm">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="bg-black border-white/20 focus:border-white text-white mt-2"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-300">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="bg-black border-white/20 focus:border-white text-white mt-2"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-gray-300">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="bg-black border-white/20 focus:border-white text-white mt-2"
                      placeholder="+383 (XX) XXX-XXXX"
                    />
                  </div>
                  <div>
                    <Label htmlFor="eventType" className="text-gray-300">Event Type *</Label>
                    <Input
                      id="eventType"
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleInputChange}
                      required
                      className="bg-black border-white/20 focus:border-white text-white mt-2"
                      placeholder="Wedding, Corporate, Music Video, etc."
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="eventDate" className="text-gray-300">Event Date</Label>
                  <Input
                    id="eventDate"
                    name="eventDate"
                    type="date"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                    className="bg-black border-white/20 focus:border-white text-white mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-300">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="bg-black border-white/20 focus:border-white text-white mt-2"
                    placeholder="Tell me about your project..."
                  />
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-white hover:bg-gray-200 text-black font-semibold py-6 text-lg transition-all duration-300 hover:scale-105"
                >
                  Send Booking Request
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="mt-12 grid md:grid-cols-3 gap-6 text-center">
            <a 
              href="mailto:Edon.stavileci@icloud.com"
              className="flex flex-col items-center p-6 bg-black/30 rounded-lg border border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <Mail className="text-white mb-2" size={24} />
              <div className="text-sm text-gray-400">Email</div>
              <div className="text-white hover:text-gray-300">{portfolioData.contact.email}</div>
            </a>
            <a 
              href="tel:+38344540570"
              className="flex flex-col items-center p-6 bg-black/30 rounded-lg border border-white/10 hover:border-white/30 transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <Phone className="text-white mb-2" size={24} />
              <div className="text-sm text-gray-400">Phone</div>
              <div className="text-white hover:text-gray-300">{portfolioData.contact.phone}</div>
            </a>
            <div className="flex flex-col items-center p-6 bg-black/30 rounded-lg border border-white/10 hover:border-white/30 transition-all duration-300">
              <MapPin className="text-white mb-2" size={24} />
              <div className="text-sm text-gray-400">Location</div>
              <div className="text-white">{portfolioData.contact.location}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 mb-4 md:mb-0">
              © 2026 {portfolioData.hero.name}. All rights reserved.
            </div>
            <div className="flex space-x-6">
              <a 
                href={portfolioData.contact.socialMedia.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                <Instagram size={24} />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
